import React, {useEffect, useState} from 'react';
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Divider,
    ListItemAvatar,
    ListItemText,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {Chat, Search} from '@mui/icons-material';
import {searchUsersByName} from '../../../../../../firebase/firestore/users';
import ExpertCard from './ExpertCard';

const ManualExpertSearch = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedExpert, setSelectedExpert] = useState(null);

    useEffect(() => {
        if (!query || query.length < 2) {
            setResults([]);
            return;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            if (cancelled) return;
            setLoading(true);
            try {
                const found = await searchUsersByName(query, {limit: 10});
                if (!cancelled) {
                    // Transform to expert-like objects
                    setResults(found.map(u => ({
                        id: u.id,
                        displayName: u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
                        title: u.title || 'Analyst',
                        office: u.office || u.department || '',
                        organization: u.organization || '',
                        email: u.email,
                        photo: u.headshot || u.photoURL,
                        specialties: u.specialties || []
                    })));
                }
            } catch (err) {
                console.error('Search error:', err);
                if (!cancelled) setResults([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query]);

    const getLabel = (u) => {
        if (!u) return '';
        return u.displayName || u.email || u.id || '';
    };

    return (
        <Stack p={2} spacing={2} height="100%">
            <Typography variant="body2" color="text.secondary">
                Search for experts by name to request their input on your report.
            </Typography>

            <Autocomplete
                size="small"
                freeSolo={false}
                options={results}
                getOptionLabel={getLabel}
                inputValue={query}
                onInputChange={(e, value, reason) => {
                    if (reason === 'reset' || reason === 'blur') return;
                    setQuery(value);
                }}
                onChange={(e, value) => {
                    if (value) setSelectedExpert(value);
                }}
                loading={loading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Search by name..."
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <Search fontSize="small" sx={{mr: 1, color: 'text.secondary'}}/>
                                        {params.InputProps.startAdornment}
                                    </>
                                )
                            }
                        }}
                    />
                )}
                renderOption={(props, option) => {
                    const {key, ...otherProps} = props;
                    return (
                        <Box component="li" key={key ?? option.id} {...otherProps}>
                            <ListItemAvatar>
                                <Avatar src={option.photo} alt={getLabel(option)}>
                                    {(option.displayName?.[0] || '?').toUpperCase()}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={getLabel(option)}
                                secondary={option.title || option.email}
                            />
                        </Box>
                    );
                }}
            />

            {selectedExpert && (
                <>
                    <Divider/>
                    <Typography variant="subtitle2" fontWeight={600}>
                        Selected Expert
                    </Typography>
                    <ExpertCard expert={selectedExpert} compact/>
                    <Button
                        variant="contained"
                        startIcon={<Chat/>}
                        fullWidth
                    >
                        Request Review
                    </Button>
                </>
            )}
        </Stack>
    );
};

export default ManualExpertSearch;
